import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for managing user notification preferences
 */
export function useNotificationPreferences() {
  const [preferences, setPreferences] = useState({
    proposal_created: true,
    voting_deadline: true,
    vote_registered: true,
    proposal_status_change: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  }, []);

  // Load preferences from backend
  const loadPreferences = useCallback(async () => {
    if (!window.t24Governance.isLoggedIn) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        action: 't24_get_notification_prefs',
        nonce: window.t24Governance.nonce
      });

      const response = await fetch(
        `${window.t24Governance.ajaxUrl}?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'same-origin'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load notification preferences');
      }

      const data = await response.json();

      if (data.success && data.data) {
        setPreferences(prev => ({
          ...prev,
          ...data.data.preferences
        }));
      }
    } catch (err) {
      console.error('Error loading notification preferences:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save preferences to backend
  const savePreferences = useCallback(async (newPreferences) => {
    if (!window.t24Governance.isLoggedIn) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('action', 't24_save_notification_prefs');
      formData.append('nonce', window.t24Governance.nonce);
      formData.append('preferences', JSON.stringify(newPreferences));

      const response = await fetch(window.t24Governance.ajaxUrl, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin'
      });

      const data = await response.json();

      if (data.success) {
        setPreferences(newPreferences);
      } else {
        throw new Error(data.data?.message || 'Failed to save preferences');
      }
    } catch (err) {
      console.error('Error saving notification preferences:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, []);

  // Toggle a single preference
  const togglePreference = useCallback((key) => {
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    };
    savePreferences(newPreferences);
  }, [preferences, savePreferences]);

  // Enable all notifications
  const enableAll = useCallback(() => {
    const newPreferences = {
      proposal_created: true,
      voting_deadline: true,
      vote_registered: true,
      proposal_status_change: true
    };
    savePreferences(newPreferences);
  }, [savePreferences]);

  // Disable all notifications
  const disableAll = useCallback(() => {
    const newPreferences = {
      proposal_created: false,
      voting_deadline: false,
      vote_registered: false,
      proposal_status_change: false
    };
    savePreferences(newPreferences);
  }, [savePreferences]);

  return {
    preferences,
    loading,
    saving,
    error,
    togglePreference,
    enableAll,
    disableAll,
    reload: loadPreferences
  };
}
